import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Globe, WarningCircle } from "@phosphor-icons/react";
import { api, fmtErr } from "../lib/api";
import { Modal } from "../pages/Contacts";

export const AutoTagModal = ({ onClose, onChanged }) => {
  const [info, setInfo] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const prevStatus = useRef(null);

  const loadReviews = () => api.autotagReviews().then(setReviews).catch(() => {});

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const d = await api.autotagLatest();
        if (stop) return;
        setInfo(d);
        if (prevStatus.current === "running" && d.job?.status !== "running") {
          loadReviews();
          onChanged?.();
        }
        prevStatus.current = d.job?.status || null;
      } catch { /* ignore */ }
    };
    tick();
    loadReviews();
    const iv = setInterval(tick, 3000);
    return () => { stop = true; clearInterval(iv); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try {
      await api.autotagStart({ force });
      const d = await api.autotagLatest();
      setInfo(d);
      prevStatus.current = "running";
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const job = info?.job;
  const running = job?.status === "running";
  const cnt = job?.counters || {};
  const remaining = info ? info.total_companies - info.analysed : 0;

  return (
    <Modal title="Auto-tag companies with AI" subtitle={info ? `${info.total_companies} companies · ${info.untyped} untyped · ${info.analysed} already analysed` : "Loading…"} onClose={onClose} wide>
      <div data-testid="autotag-modal" className="space-y-4">
        {!running && (
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 text-sm text-zinc-600 space-y-2">
            <p>For each company, MediHub finds its website from your contacts' email domains, reads the homepage, writes a short description and assigns a <strong>Type</strong> plus <strong>capability tags</strong> (does: Market Research, does: Clinical Trials…). Confident results apply automatically — and propagate to every contact at the company; uncertain ones queue below for your review. Your manually-set Types are never overwritten.</p>
            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
              <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} data-testid="autotag-force" />
              Re-analyse companies that were already processed
            </label>
            <button onClick={start} disabled={busy || !info || (!force && remaining === 0)} data-testid="autotag-start"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
              <Sparkle size={14} />{busy ? "Starting…" : info ? `Analyse ${force ? info.total_companies : remaining} compan${(force ? info.total_companies : remaining) === 1 ? "y" : "ies"}` : "…"}
            </button>
            {!force && remaining === 0 && info && <span className="text-xs text-zinc-400 ml-2">everything analysed — tick re-analyse to run again</span>}
          </div>
        )}

        {running && (
          <div className="rounded-lg border border-zinc-200 p-3" data-testid="autotag-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-zinc-900">Analysing companies…</span>
              <span className="text-zinc-500 tabular-nums">{job.progress?.done || 0} / {job.progress?.total || 0}</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-zinc-100 overflow-hidden">
              <div className="h-full bg-zinc-950 transition-all" style={{ width: `${job.progress?.total ? Math.round(100 * (job.progress.done || 0) / job.progress.total) : 0}%` }} />
            </div>
            <p className="text-xs text-zinc-400 mt-2">Reads each website + AI profiling — roughly 10-20 minutes for a full run. Safe to close this window; the job keeps going.</p>
            <div className="mt-2 text-xs text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
              <span>{cnt.applied_types || 0} types applied</span>
              <span>{cnt.capabilities_applied || 0} capability-tagged</span>
              <span>{cnt.queued_review || 0} queued for review</span>
              <span>{(cnt.no_domain || 0) + (cnt.site_failed || 0)} without a readable website</span>
            </div>
          </div>
        )}

        {!running && job && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-sm" data-testid="autotag-summary">
            <div className="font-medium text-zinc-900">
              Last run {job.status === "done" ? "complete" : job.status === "interrupted" ? "interrupted — it resumes automatically, check back in a moment" : "stopped early — press Analyse to continue where it left off"} · {job.progress?.done || 0} companies analysed
            </div>
            <div className="mt-1 text-xs text-zinc-600 flex flex-wrap gap-x-4 gap-y-1">
              <span>{cnt.described || 0} descriptions written</span>
              <span>{cnt.applied_types || 0} types auto-applied</span>
              <span>{cnt.capabilities_applied || 0} capability-tagged</span>
              <span>{cnt.queued_review || 0} queued for review</span>
              <span>{cnt.no_domain || 0} no website found</span>
              <span>{cnt.site_failed || 0} website unreachable</span>
            </div>
          </div>
        )}

        {reviews?.items?.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
              <WarningCircle size={15} className="text-amber-500" /> Needs your review ({reviews.items.length})
            </div>
            <div className="space-y-2 max-h-[45vh] overflow-auto pr-1" data-testid="autotag-review-list">
              {reviews.items.map(r => (
                <ReviewCard key={r.id} r={r} onDone={() => { loadReviews(); onChanged?.(); }} />
              ))}
            </div>
          </div>
        )}
        {reviews && reviews.items?.length === 0 && job && !running && (
          <p className="text-xs text-zinc-400" data-testid="autotag-no-reviews">Nothing waiting for review.</p>
        )}
      </div>
    </Modal>
  );
};

function ReviewCard({ r, onDone }) {
  const [type, setType] = useState(r.proposed_type || "");
  const [caps, setCaps] = useState(new Set(r.capabilities || []));
  const [busy, setBusy] = useState(false);

  const act = async (action) => {
    setBusy(true);
    try {
      const payload = action === "accept" ? { action, company_type: type, capabilities: [...caps] } : { action };
      const res = await api.autotagReviewAction(r.id, payload);
      toast.success(action === "accept" ? `Applied to ${r.name}${res.retagged_contacts ? ` · ${res.retagged_contacts} contacts re-tagged` : ""}` : "Dismissed");
      onDone();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid={`autotag-review-${r.name}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium text-zinc-900">{r.name}</span>
        {r.domain && <a href={`https://${r.domain}`} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-400 hover:text-zinc-600 inline-flex items-center gap-0.5"><Globe size={11} />{r.domain}</a>}
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{r.confidence}</span>
        <span className="text-[11px] text-zinc-400">{r.reason}</span>
      </div>
      {r.description && <p className="text-xs text-zinc-500 mt-1">{r.description}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {r.current_type && <span className="text-xs text-zinc-400">Current: <span className="text-zinc-600">{r.current_type}</span> →</span>}
        <input value={type} onChange={e => setType(e.target.value)} data-testid={`review-type-${r.name}`}
          className="h-7 w-64 rounded-md border border-zinc-200 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-zinc-950" placeholder="Type (comma-separated)" />
      </div>
      {(r.capabilities || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {r.capabilities.map(t => {
            const on = caps.has(t);
            return (
              <button key={t} type="button" onClick={() => setCaps(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; })}
                className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${on ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-white text-zinc-400 border-zinc-200 line-through"}`}>
                {t.replace("does: ", "")}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-2.5 flex items-center gap-2">
        <button onClick={() => act("accept")} disabled={busy} data-testid={`review-accept-${r.name}`}
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">{busy ? "…" : "Accept & apply"}</button>
        <button onClick={() => act("dismiss")} disabled={busy} data-testid={`review-dismiss-${r.name}`}
          className="text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50">Dismiss</button>
      </div>
    </div>
  );
}

export default AutoTagModal;
